<template>
    <form
        class="address-input"
        ref="form"
        @submit.prevent="handleUpdate"
    >
        <fieldset>
            <div class="mb-6">
                <label :for="`${state.id}:name`">
                    Full Name
                </label>
                <input
                    :id="`${state.id}:name`"
                    v-model="state.fullName"
                    type="text"
                    name="name"
                    autocomplete="name"
                    placeholder="Full Name"
                    required
                />
            </div>

            <div class="mb-6">
                <label :for="`${state.id}:name`" class="font-semibold text-normal block mb-2">
                    Phone number
                </label>
                <input
                    :id="`${state.id}:phone`"
                    v-model="state.phone"
                    type="text"
                    name="phone"
                    autocomplete="phone"
                    placeholder="+1 (555) 987-6543"
                />
            </div>

            <div class="-space-y-px rounded-md">
                <div class="address-input__line">
                    <label :for="`${state.id}:country`" class="sr-only"> Country </label>
                    <select
                        :id="`${state.id}:country`"
                        v-model="state.address.country"
                        :class="{ 'ring-red-700': !!state.validate.address.country }"
                        name="country"
                        autocomplete="country-name"
                        class="relative block w-full rounded-none rounded-t-md border-0 py-2.5 text-zinc-900 ring-1 ring-inset ring-gray-500 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                    >
                        <option value="" />
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="MX">Mexico</option>
                    </select>
                </div>

                <div class="address-input__line">
                    <label :for="`${state.id}:line1`" class="sr-only">Address line 1</label>
                    <input
                        :id="`${state.id}:line1`"
                        v-model="state.address.line1"
                        type="text"
                        name="address-line1"
                        class="address relative block w-full rounded-none border-0 py-2.5 text-zinc-900 ring-1 ring-inset ring-gray-500 placeholder:text-zinc-500 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                        autocomplete="address-line1"
                        placeholder="Address line 1"
                        required
                    />
                </div>

                <div class="address-input__line">
                    <label :for="`${state.id}:line2`" class="sr-only">Address line 2</label>
                    <input
                        :id="`${state.id}:line2`"
                        v-model="state.address.line2"
                        type="text"
                        name="address-line2"
                        class="address relative block w-full rounded-none border-0 py-2.5 text-zinc-900 ring-1 ring-inset ring-gray-500 placeholder:text-zinc-500 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                        autocomplete="address-line2"
                        placeholder="Address line 2"
                    />
                </div>

                <div class="flex address-input__line">
                    <div class="flex-grow">
                        <label :for="`${state.id}:city`" class="sr-only">City</label>
                        <input
                            :id="`${state.id}:city`"
                            v-model="state.address.city"
                            type="text"
                            name="address-city"
                            class="address relative block w-full rounded-none border-0 py-2.5 text-zinc-900 ring-1 ring-inset ring-gray-500 placeholder:text-zinc-500 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                            autocomplete="address-level2"
                            placeholder="City"
                        />
                    </div>

                    <div>
                        <label :for="`${state.id}:state`" class="sr-only">State</label>
                        <select
                            :id="`${state.id}:state`"
                            v-model="state.address.state"
                            name="state"
                            autocomplete="address-level1"
                            class="relative block w-full rounded-none border-0 py-2.5 text-zinc-900 ring-1 ring-inset ring-gray-500 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                            placeholder="State"
                        >
                            <option v-for="state in states" :key="state.value" :value="state.value">
                                {{ state.label }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="address-input__line">
                    <label :for="`${state.id}:postal_code`" class="sr-only">Postal code</label>
                    <input
                        :id="`${state.id}:postal_code`"
                        v-model="state.address.postal_code"
                        type="text"
                        name="postal-code"
                        autocomplete="postal-code"
                        class="address relative block w-full rounded-none border-0 py-2.5 text-zinc-900 ring-1 ring-inset ring-gray-500 placeholder:text-zinc-500 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                        placeholder="ZIP"
                        :required="state.address.country === 'US'"
                    />
                </div>
            </div>
        </fieldset>
        <div class="mt-6">
            <button
                type="submit"
                class="button button--primary w-full"
            >
                Save
            </button>
        </div>
    </form>
</template>

<script setup lang="ts">
import { reactive, ref, getCurrentInstance, onBeforeMount } from 'vue'
import states from '@/data/states.json'

interface Props {
    address?: AddressFields;
    fullName: string;
    phone: string;
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (event: 'update', value: { address: AddressFields, fullName: string, phone: string }): void,
}>()
const formRef = ref<HTMLFormElement | null>(null)

const state = reactive({
    id: 0,
    fullName: props.fullName || '',
    phone: props.phone || '',
    address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        ...(props.address || {}),
    },
    validate: {
        fullName: false,
        address: {
            line1: false,
            city: false,
            state: false,
            postal_code: false,
            country: false,
        },
    },
})

const validateAddress = (): boolean => {
    state.validate.address.line1 = !!state.address.line1
    state.validate.address.city = !!state.address.city
    state.validate.address.state = state.address.country === 'US'
        ? !!state.address.state && state.address.state.toString().length === 2
        : true
    state.validate.address.postal_code = !isNaN(Number(state.address.postal_code)) && state.address.postal_code.toString().length === 5
    state.validate.address.country = !!state.address.country && state.address.country.toString().length === 2
    return Object.values(state.validate.address).every(Boolean)
}

const validate = (): boolean => {
    state.validate.fullName = !!state.fullName
    return validateAddress() && state.validate.fullName
}

const handleUpdate = (): void => {
    const isValid = validate()
    if (!isValid) {
        formRef.value?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        })
    }
    emit('update', {
        fullName: state.fullName,
        phone: state.phone,
        address: state.address,
    })
}

onBeforeMount(() => {
    state.id = getCurrentInstance()?.uid || 0
})
</script>

<style>
.address-input__line:last-child input {
    @apply rounded-b-md;
}
</style>
